import DataTable, { TableColumn } from 'react-data-table-component';
import SelectCopyFromModal from '../modal/SelectCopyFromModal';
import { useState, useEffect } from 'react';
import reportApi from '../../api/CallCreateReportApi';
import { toast } from 'react-toastify';
import { toastConfig, getMediaTitle } from '../../utils';
import { Report } from '../../interface/Report';
import RowButtonsComponent from './RowButtonsComponent';

interface TableProps {
  reports: Report[];
  selectRow: ({ selectedRows }: { selectedRows: any }) => void;
  isClearSelectedRow: boolean;
  moveConfirmPage: (record: any) => void;
  moveEditPage: (record: any) => void;
  moveCopyLessonPage: (record: any) => void;
  moveCopyLessonAndMediaPage: (record: any) => void;
  isDisplayEdit: boolean;
  isDisplayCopy: boolean;
  hideStatusColumn?: boolean;
  selectableRowsVisibleOnly: boolean;
}

const Table: React.FC<TableProps> = ({
  reports,
  selectRow,
  isClearSelectedRow,
  moveConfirmPage,
  moveEditPage,
  hideStatusColumn,
  moveCopyLessonPage,
  moveCopyLessonAndMediaPage,
  isDisplayEdit,
  isDisplayCopy,
  selectableRowsVisibleOnly,
}) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>({});

  const showCopyFromModal = (record: any) => {
    setShowModal(true);
    setSelectedRow(record);
  };

  const selectCopyFrom = async (from: string) => {
    setShowModal(false);
    if (from === 'class') {
      moveCopyLessonPage(selectedRow);
    } else {
      if (!(await reportApi.getSubCategory(selectedRow.media.categories))) {
        toast.error('コピーできない利用報告です。\n新規登録よりご報告ください。', toastConfig());
        return;
      }
      moveCopyLessonAndMediaPage(selectedRow);
    }
  };

  const getDisplayText = (text: string) => {
    if (!text) return '';
    if (text.length > 15) return `${text.slice(0, 15)}...`;
    return text;
  };

  const baseColumns: Array<TableColumn<any>> = [
    {
      name: '授業情報',
      reorder: false,
      maxWidth: '10rem',
      sortable: true,
      selector: (row) => row.classInfo.subject + ' ' + String(row.classInfo.grade) + '学年' + ' ' + row.classInfo.personCount + '人',
      format: (row) => getDisplayText(row.classInfo.subject + ' ' + String(row.classInfo.grade) + '学年' + ' ' + row.classInfo.personCount + '人'),
      wrap: true,
    },
    {
      name: '入手・掲載元種別',
      reorder: false,
      minWidth: '130px',
      selector: (row) => row.media.categories[0],
      sortable: true,
      wrap: true,
      format: (row) => getDisplayText(row.media.categories[0]),
    },
    {
      name: '入手・掲載元の名称',
      reorder: false,
      minWidth: '130px',
      sortable: true,
      wrap: true,
      format: (row) => getDisplayText(getMediaTitle(row)),
      selector: (row) => getDisplayText(getMediaTitle(row)),
    },
    {
      name: '著作物の分類',
      reorder: false,
      maxWidth: '12rem',
      selector: (row) => row.work.subCategory.slice(2),
      format: (row) => getDisplayText(row.work.subCategory.slice(2)),
      sortable: true,
      wrap: true,
    },
    {
      name: '差戻理由',
      reorder: false,
      maxWidth: '12rem',
      sortable: true,
      wrap: true,
      selector: (row) => (row.comment ? row.comment.content : ''),
      format: (row) => getDisplayText(row.comment ? row.comment.content : ''),
    },
    {
      name: '',
      sortable: false,
      reorder: false,
      minWidth: '150px',
      cell: (record) => {
        return (
          <RowButtonsComponent
            isDisplayConfirmButton={true}
            isDisplayEditButton={isDisplayEdit}
            isDisplayCopyButton={isDisplayCopy}
            handleClickConfirmButton={() => moveConfirmPage(record)}
            handleClickEditButton={() => moveEditPage(record)}
            handleClickCopyButton={() => showCopyFromModal(record)}
          />
        );
      },
    },
  ];
  const [columns, setColumns] = useState<Array<TableColumn<any>>>(baseColumns);

  useEffect(() => {
    setColumns(baseColumns);
  }, [isDisplayEdit]);

  return (
    <>
      <DataTable
        className="mt-1"
        columns={columns}
        data={reports}
        selectableRows={true}
        onSelectedRowsChange={selectRow}
        pagination
        paginationResetDefaultPage={false}
        clearSelectedRows={isClearSelectedRow}
        noDataComponent={<div className="reports-no-data">条件に一致する利用報告はありません。</div>}
        responsive={true}
        selectableRowsVisibleOnly={selectableRowsVisibleOnly}
      />
      <SelectCopyFromModal show={showModal} setShow={setShowModal} nextStep={selectCopyFrom} />
    </>
  );
};

export default Table;
